// Attach a name — turning "Unnamed (legacy)" into real counterparties.
//
// WHY THERE IS A QUEUE AT ALL. On an own-company trade legacy records the DESK
// as the party, and the actual counterparty exists only as free text in the
// description: "Endrias", "Abrar", "Pushpendra". Of the nineteen distinct names
// since cutover, three existed as parties. The rest were never modelled
// anywhere — they were typed into a box.
//
// So the mirror books them against `unnamed-legacy`, which holds ZERO by
// construction (a trade posts all four legs, so the party nets out). That is
// what makes leaving them here safe: the placeholder cannot quietly accumulate
// value while the names are unresolved.
//
// WHY THE SUGGESTIONS ARE SO CONSERVATIVE. Substring matching is dangerous here,
// measurably: "Ash" matches cash.aed.dxb and "Han" matches thanos. Either would
// book a real trade against a treasury rail and look internally consistent
// afterwards. So a suggestion appears only on an EXACT or uniquely
// case-insensitive match — the same rule the ingester uses. Everything else is
// the operator's call, because only they know whether "Ash" is "Ashar".
//
// ATTACHING IS A RELABEL, NOT A TRANSFER. Nothing moves. Getting one wrong costs
// a correction, not a reconciliation — which is why this screen lets you work
// quickly instead of making you confirm twice.

import { useState } from 'react';
import { Pending } from '../components/Pending';
import { EmptyState } from '../components/kit';
import { LAST_READ_NONE, ReadFailed, Refused } from '../components/OnboardingParts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, SkipForward } from 'lucide-react';
import { api } from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { Pill } from '../components/Pill';
import type { UnnamedGroup, UnnamedResponse } from '../lib/api';
import { fmtAmount, fmtDay } from '../lib/format';
import { partyName } from '../lib/naming';

function since(iso: string | null): string {
  if (!iso) return '—';
  return fmtDay(iso);
}

const HELP = (
  <>
    <p>
      Legacy records your desk as the party and the counterparty only as free text.
      Attaching a name relabels those trades and teaches the mirror, so the next trade for
      that name resolves on its own. Nothing moves — the placeholder holds zero.
    </p>
    <p>
      A suggestion appears only on an exact or uniquely case-insensitive match; everything
      else is your call. Getting one wrong costs a correction, not a reconciliation.
    </p>
  </>
);

export function AttachNames() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [picked, setPicked] = useState<Record<string, string>>({});

  const { data, isPending, error, refetch } = useQuery<UnnamedResponse>({
    queryKey: ['unnamed'],
    queryFn: () => api.unnamedNames(),
  });

  const attach = useMutation({
    mutationFn: (v: { name_key: string; name_seen: string; party_slug?: string; ignore?: boolean }) =>
      api.attachName(v),
    onSettled: () => {
      setBusy(null);
      // The list is served through Hyperdrive, which caches reads — so a refetch
      // straight after a write can legitimately return the PRE-write list. The
      // row disappearing a beat late is the cache, not a failed save.
      setTimeout(() => qc.invalidateQueries({ queryKey: ['unnamed'] }), 1200);
    },
  });

  // THE TITLE STAYS while the queue is read and when it could not be (22 Oct 2026): a bare red sentence with no title and
  // no way out was all a failed read left.
  // PENDING, not "loading": a first read opened with no connection is paused (no answer, nothing fetching) and waits.
  if (isPending) return <div className="space-y-4"><ScreenHeader title="Names" help={HELP} /><Pending /></div>;
  // The read said why it failed; "could not load the queue" threw that away and
  // left a dead tunnel looking the same as an expired sign-in. Failure is asked
  // about FIRST: no data and no error is a read still on its way (a paused
  // query while the phone is offline), and Pending is what says so.
  // …but only when there is nothing to show. This screen refetches 1.2 s after
  // every write, so putting `error` first replaced the whole queue with one red
  // line whenever that refetch blipped, losing the operator's place mid-pass.
  if (error && !data) {
    return (
      <div className="space-y-4">
        <ScreenHeader title="Names" help={HELP} />
        <ReadFailed what="the names" error={error} retry={() => { void refetch(); }} />
      </div>
    );
  }
  if (!data) return <div className="space-y-4"><ScreenHeader title="Names" help={HELP} /><Pending /></div>;

  const open = data.groups.filter((g) => !g.resolved && !g.ignored);
  const done = data.groups.filter((g) => g.resolved || g.ignored);

  // A REFUSAL BELONGS TO THE ROW IT REFUSED. The sentence used to render above
  // the header while the operator was looking at the name they had just
  // clicked, and `busy` is cleared on settle, so nothing on screen said WHICH
  // name failed — on a queue of nineteen that is a guess. The row carries it
  // now; the header keeps it only when the refused row is no longer listed
  // (a refetch landed between the failure and this render).
  const failedKey = attach.isError ? attach.variables?.name_key ?? null : null;
  const failedOnScreen = failedKey !== null && open.some((g) => g.name_key === failedKey);

  const run = (g: UnnamedGroup, ignore: boolean) => {
    const slug = picked[g.name_key] ?? g.suggestion?.slug;
    if (!ignore && !slug) return;
    setBusy(g.name_key);
    attach.mutate({ name_key: g.name_key, name_seen: g.name_seen, party_slug: ignore ? undefined : slug, ignore });
  };

  // THE PHONE SHAPE (19 Sept 2026, the phone-layout sweep). Each open name is a
  // .card: line 1 the name with its "Matches …" pill at the line's end, line 2
  // the muted facts, then the picker full width and the two buttons side by
  // side — the same two-column pair every entry form uses below lg. At lg+
  // the row keeps its one-line header and inline controls. The borders used
  // to name `tg-section`, a token the palette does not hold (only
  // `tg-section-bg` exists), so every box here drew Tailwind's default
  // light-grey outline. Below lg the boxes, the picker and the buttons wear
  // the house classes (max-lg:card / max-lg:select / max-lg:btn-secondary),
  // whose borders are real; at lg+ they keep their old shape — outline box,
  // 14px picker, transparent second button — with the border colour cured
  // and nothing else changed. The house classes are gated to max-lg on
  // purpose: styles.css restyles every `.select` at lg+ from AFTER the
  // utilities layer, so an lg: utility on a `.select` element would lose.
  return (
    <div className="space-y-4">
      {/* The count stands only when there is something to count: "0 names to attach" over "Nothing waiting" said it twice. */}
      <ScreenHeader
        title="Names"
        subtitle={open.length > 0 ? <>{open.length} name{open.length === 1 ? '' : 's'} to attach to legacy trades.</> : undefined}
        help={HELP}
      />
      {error && (
        <ReadFailed what="the names" error={error} says={open.length === 0 ? LAST_READ_NONE : 'showing the last good read'} retry={() => { void refetch(); }} />
      )}
      {attach.isError && !failedOnScreen && <Refused error={attach.error} />}
      {/* "Every trade is named" is an all-clear: never over a refresh that failed — the line above says what the LAST read showed. */}
      {open.length === 0 && !error && <EmptyState>Nothing waiting — every trade is named.</EmptyState>}

      <div className="space-y-2">
        {open.map((g) => {
          const slug = picked[g.name_key] ?? g.suggestion?.slug ?? '';
          return (
            <div key={g.name_key} className="max-lg:card lg:rounded-lg lg:border lg:border-stroke lg:p-3">
              {/* Below lg: the name and the pill share line 1 (the name
                  truncates, the pill keeps its width), the facts take line
                  2 on their own (basis-full). At lg+ the three wrap in one
                  line in their written order, as before. Every figure on the
                  facts line — the count and the volume — sits in .amt. */}
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="who text-body max-lg:order-1 max-lg:min-w-0 max-lg:flex-1 max-lg:break-words">{g.name_seen}</span>
                <span className="text-meta text-tg-hint max-lg:order-3 max-lg:basis-full">
                  <span className="amt">{g.deals}</span> trade{Number(g.deals) === 1 ? '' : 's'}
                  {g.volume_quote && <> · <span className="whitespace-nowrap"><span className="amt">{fmtAmount(g.volume_quote, g.quote_ccy ?? undefined)}</span> {g.quote_ccy}</span></>}
                  {' · '}{since(g.first_seen)}–{since(g.last_seen)}
                </span>
                {g.suggestion && (
                  <Pill tone="good" className="shrink-0 max-lg:order-2">Suggested</Pill>
                )}
              </div>

              {/* Below lg: the picker across, then the two buttons as a
                  pair; at lg+ the three sit inline in one row. */}
              <div className="mt-2 grid grid-cols-2 gap-2 lg:flex lg:flex-wrap lg:items-center">
                <select
                  value={slug}
                  onChange={(e) => setPicked({ ...picked, [g.name_key]: e.target.value })}
                  className="col-span-2 bg-tg-bg text-field lg:text-body text-tg-text max-lg:select max-lg:py-2.5 lg:min-w-[12rem] lg:rounded-lg lg:border lg:border-stroke lg:px-2 lg:py-1.5"
                >
                  <option value="">Who is this?</option>
                  {data.parties.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {partyName(p)} · {p.party_type === 'counterparty' ? 'merchant' : 'customer'}
                    </option>
                  ))}
                </select>

                <button
                  disabled={!slug || busy === g.name_key}
                  onClick={() => run(g, false)}
                  className="btn-secondary inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-body lg:py-1.5"
                >
                  <Check size={14} /> Attach
                </button>

                <button
                  disabled={busy === g.name_key}
                  onClick={() => run(g, true)}
                  title="Not a counterparty — a note, a reference, or something that should never resolve"
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-body text-tg-hint disabled:opacity-40 max-lg:btn-secondary lg:border lg:border-stroke lg:py-1.5"
                >
                  <SkipForward size={14} /> Not a name
                </button>
              </div>

              {failedKey === g.name_key && <Refused error={attach.error} className="mt-2" />}
            </div>
          );
        })}
      </div>

      {done.length > 0 && (
        <details className="rounded-xl border border-tg-secondary lg:rounded-lg lg:border-stroke">
          <summary className="cursor-pointer px-3 py-2 text-body text-tg-hint">
            {done.length} already decided
          </summary>
          {/* One line per name: the name truncates, the decision keeps its
              width — the party by its NAME (the slug is not for the screen).
              Below lg the rows carry the LEDGER.card hairline between them;
              at lg+ the list is unlined, as it was. */}
          <div className="border-t border-tg-secondary max-lg:divide-y max-lg:divide-tg-secondary lg:border-stroke">
            {done.map((g) => (
              <div key={g.name_key} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 px-3 py-2 text-body">
                <span className="text-tg-text min-w-0 break-words">{g.name_seen}</span>
                <span className="shrink-0 text-meta text-tg-hint">
                  {g.ignored ? 'not a name' : `→ ${partyName({ slug: g.attached_slug })}`} · <span className="amt">{g.deals}</span> {Number(g.deals) === 1 ? 'trade' : 'trades'}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
